import { action, computed, makeObservable, observable } from 'mobx'
import { computedFn } from 'mobx-utils'

import { IMarkup, IMarkupInput } from '~/client/graph'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Markup from '~/client/src/shared/models/Markup'
import Workflow from '~/client/src/shared/models/Workflow'
import {
  DELETE_MARKUP,
  LOAD_MARKUPS,
  SAVE_MARKUP,
} from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import Guard from '~/client/src/shared/utils/Guard'
import { toast } from '~/client/src/shared/utils/toast'

export default class MarkupsStore {
  @observable public isDataReceived = false

  // Bridge set by the active Logistics view: workflow id -> SitePermit for
  // workflows currently visible per the active filters + date range. `null` =
  // no filter context is driving it (callers fall back to showing all). Read by
  // the Precise Location modal to scope/label OTHER workflows' markups and show
  // their info card, without coupling the shared form tree to the desktop/mobile
  // Logistics stores.
  @observable.ref public visibleWorkflows: Map<string, Workflow> | null = null

  // Workflows already fetched — the scoped loader skips these and the live-update gate
  // ignores markups for workflows not here.
  private readonly loadedSitePermitIds = new Set<string>()

  // Locally deleted ids whose DeleteMarkup is still in flight — stops a racing
  // MARKUP_UPDATED from resurrecting them. Cleared when the delete settles.
  private readonly recentlyDeletedIds = new Set<string>()

  // Pinned by the open Precise Location modal so eviction can't drop its workflow.
  private editingSitePermitId: string | null = null

  public constructor(private eventsStore: EventsStore) {
    makeObservable(this)
    Guard.requireAll({ eventsStore })
  }

  @action.bound
  public setVisibleWorkflows(workflows: Map<string, Workflow> | null): void {
    this.visibleWorkflows = workflows
  }

  public get byId() {
    return this.eventsStore.appState.markups
  }

  @computed
  public get list(): Markup[] {
    return Array.from(this.byId.values())
  }

  // Markups belonging to a real, submitted workflow. Excludes the creation-phase
  // buffer (isDraft) so unsubmitted markups never show on the read-only map overlay.
  @computed
  public get persistedList(): Markup[] {
    return this.list.filter(markup => !markup.isDraft)
  }

  // Creation-phase buffer (isDraft) — shown dimmed on the map while a workflow
  // is being created; flushed (→ persisted) on submit, discarded on cancel.
  @computed
  public get draftList(): Markup[] {
    return this.list.filter(markup => markup.isDraft)
  }

  public getBySitePermitId(sitePermitId: string): Markup[] {
    return this.list.filter(markup => markup.sitePermitId === sitePermitId)
  }

  // Exact-match scope; empty when no globe is active (avoids null === null matching
  // legacy rows, and leaking every globe's markups).
  public forGlobe(
    markups: Markup[],
    globeViewId: string | undefined | null,
  ): Markup[] {
    if (!globeViewId) return []
    return markups.filter(markup => markup.globeViewId === globeViewId)
  }

  // Stable-ref per-globe lists for the map display layer: computedFn memoizes by globeViewId,
  // returning the same array across camera-frame re-renders until the inputs actually change.
  public visibleMarkupsForGlobe = computedFn(
    (globeViewId: string): Markup[] => {
      const inGlobe = this.forGlobe(this.persistedList, globeViewId)
      const visible = this.visibleWorkflows
      return visible
        ? inGlobe.filter(markup => visible.has(markup.sitePermitId))
        : inGlobe
    },
  )

  // Live-session drafts are rendered by terra-draw itself — exclude to avoid a
  // double render.
  public draftMarkupsForGlobe = computedFn(
    (globeViewId: string, excludeSitePermitId?: string): Markup[] => {
      const drafts = this.forGlobe(this.draftList, globeViewId)
      return excludeSitePermitId
        ? drafts.filter(markup => markup.sitePermitId !== excludeSitePermitId)
        : drafts
    },
  )

  @action.bound
  public clearList() {
    this.isDataReceived = false
    this.byId.clear()
    this.loadedSitePermitIds.clear()
    this.recentlyDeletedIds.clear()
    this.editingSitePermitId = null
  }

  @action.bound
  public receiveList(list: IMarkup[]) {
    this.clearList()

    list.forEach(dto => {
      const markup = Markup.fromDto(dto)
      this.byId.set(markup.id, markup)
    })

    this.isDataReceived = true
  }

  // Upsert without clearing; mark requested workflows loaded (incl. empty ones, so they
  // aren't re-fetched).
  @action.bound
  public mergeList(list: IMarkup[], sitePermitIds: string[] = []) {
    list.forEach(dto => this.byId.set(dto.id, Markup.fromDto(dto)))
    sitePermitIds.forEach(id => this.loadedSitePermitIds.add(id))
    list.forEach(dto => this.loadedSitePermitIds.add(dto.sitePermitId))
    this.isDataReceived = true
  }

  public isSitePermitLoaded(sitePermitId: string): boolean {
    return this.loadedSitePermitIds.has(sitePermitId)
  }

  // Load only not-yet-loaded workflows — keeps the store scoped to the current view.
  public ensureLoadedForSitePermits(sitePermitIds: string[]): void {
    const toLoad = sitePermitIds.filter(
      id => id && !this.loadedSitePermitIds.has(id),
    )
    if (toLoad.length) {
      this.eventsStore.dispatch(LOAD_MARKUPS, toLoad)
    }
  }

  @action.bound
  public setEditingSitePermitId(sitePermitId: string | null): void {
    this.editingSitePermitId = sitePermitId
  }

  // Evict workflows no longer in view so memory tracks the current view, not the session
  // union. Keeps unsaved drafts and the pinned edited workflow; evicted ones re-fetch.
  @action.bound
  public retainSitePermits(visibleIds: string[]): void {
    const keep = new Set(visibleIds)
    if (this.editingSitePermitId) keep.add(this.editingSitePermitId)

    const toEvict: string[] = []
    this.byId.forEach(markup => {
      if (!markup.isDraft && !keep.has(markup.sitePermitId)) {
        toEvict.push(markup.id)
      }
    })
    toEvict.forEach(id => this.byId.delete(id))

    Array.from(this.loadedSitePermitIds).forEach(id => {
      if (!keep.has(id)) this.loadedSitePermitIds.delete(id)
    })
  }

  @action.bound
  public receiveOne(id: string, dto: IMarkup) {
    // A live update must not resurrect a markup whose delete is still in flight.
    if (this.recentlyDeletedIds.has(id)) {
      return
    }
    if (dto && !dto.isDeleted) {
      this.byId.set(dto.id, Markup.fromDto(dto))
    } else {
      this.byId.delete(id)
    }
  }

  @action.bound
  public save(
    markup: Markup,
    callbackFn?: (id: string) => void,
    errorCallbackFn?: (error: unknown) => void,
  ) {
    const { id: projectId } = this.eventsStore.appState.activeProject

    const markupInput: IMarkupInput = {
      id: markup.id,
      projectId,
      sitePermitId: markup.sitePermitId,
      globeViewId: markup.globeViewId,
      kind: markup.kind,
      geometryType: markup.geometryType,
      coordinates: markup.coordinates,
      color: markup.color,
      strokeWidth: markup.strokeWidth,
      opacity: markup.opacity,
      fillColor: markup.fillColor,
      text: markup.text,
      strokeStyle: markup.strokeStyle,
      fillStyle: markup.fillStyle,
      shapeKind: markup.shapeKind,
      lineType: markup.lineType,
      arrowheadStart: markup.arrowheadStart,
      arrowheadEnd: markup.arrowheadEnd,
      fontSize: markup.fontSize,
      bold: markup.bold,
      italic: markup.italic,
      underline: markup.underline,
      textAlign: markup.textAlign,
      boxWidth: markup.boxWidth,
      rotation: markup.rotation,
    }

    // Only a persisted edit restores (different instance); new or draft-flush ⇒ remove.
    const previous = this.byId.get(markup.id)
    const restoreOnFailure =
      previous && previous !== markup && !previous.isDraft
        ? previous
        : undefined

    this.byId.set(markup.id, markup)
    // Saving a markup means its workflow is now real + relevant, so mark it loaded:
    // live MARKUP_UPDATED events for it then pass the relevance gate (covers the
    // just-created-workflow case after flushPending re-keys to the real id).
    if (markup.sitePermitId) this.loadedSitePermitIds.add(markup.sitePermitId)

    this.eventsStore.dispatch(SAVE_MARKUP, markupInput, callbackFn, error => {
      this.handleSaveFailure(markup.id, restoreOnFailure)
      errorCallbackFn?.(error)
    })
  }

  @action.bound
  private handleSaveFailure(id: string, previous?: Markup) {
    if (previous) {
      this.byId.set(id, previous)
    } else {
      this.removeLocal([id])
    }
    toast.error(Localization.translator.failedToSaveChangesTryAgain, {
      key: 'markup-save-failed',
    })
  }

  // A failed DeleteMarkup left the markup gone locally but alive on the server —
  // restore it and tell the user (mirrors handleSaveFailure).
  @action.bound
  private handleDeleteFailure(markup?: Markup) {
    if (!markup) {
      return
    }
    this.recentlyDeletedIds.delete(markup.id)
    this.byId.set(markup.id, markup)
    toast.error(Localization.translator.failedToSaveChangesTryAgain, {
      key: 'markup-delete-failed',
    })
  }

  // While creating a workflow we have no real sitePermitId yet, so markups are
  // cached under a client temp id WITHOUT dispatching SaveMarkup. Once the
  // workflow is created we re-key them to the real id and persist (flushPending);
  // cancelling creation just drops them (discardPending). Mirrors pendingUploadLogs.

  @action.bound
  public savePending(markup: Markup) {
    markup.isDraft = true
    this.byId.set(markup.id, markup)
  }

  @action.bound
  public removeLocal(ids: string[]) {
    ids?.forEach(id => this.byId.delete(id))
  }

  // Shapes that live only in an editor are reachable solely through its committer.
  private readonly pendingCommitters = new Map<string, () => void>()

  public registerPendingCommitter(
    tempSitePermitId: string,
    commit: () => void,
  ): void {
    if (tempSitePermitId) {
      this.pendingCommitters.set(tempSitePermitId, commit)
    }
  }

  public unregisterPendingCommitter(tempSitePermitId: string): void {
    this.pendingCommitters.delete(tempSitePermitId)
  }

  /** Call before reading a draft buffer that another surface may still be drawing into. */
  public commitPending(tempSitePermitId: string): void {
    this.pendingCommitters.get(tempSitePermitId)?.()
  }

  /** False once the buffer was flushed or discarded: nobody owns it any more. */
  public hasPendingCommitter(tempSitePermitId: string): boolean {
    return this.pendingCommitters.has(tempSitePermitId)
  }

  @action.bound
  public flushPending(tempSitePermitId: string, realSitePermitId: string) {
    if (!tempSitePermitId || !realSitePermitId) {
      return
    }
    this.pendingCommitters.delete(tempSitePermitId)
    this.getBySitePermitId(tempSitePermitId).forEach(markup => {
      markup.sitePermitId = realSitePermitId
      markup.isDraft = false
      this.save(markup)
    })
  }

  @action.bound
  public discardPending(tempSitePermitId: string) {
    if (!tempSitePermitId) {
      return
    }
    this.pendingCommitters.delete(tempSitePermitId)
    this.getBySitePermitId(tempSitePermitId).forEach(markup =>
      this.byId.delete(markup.id),
    )
  }

  @action.bound
  public remove(id: string, callbackFn?: () => void) {
    if (!id) {
      return
    }

    const markup = this.byId.get(id)
    this.byId.delete(id)
    this.recentlyDeletedIds.add(id)

    this.eventsStore.dispatch(
      DELETE_MARKUP,
      id,
      () => {
        this.recentlyDeletedIds.delete(id)
        callbackFn?.()
      },
      () => this.handleDeleteFailure(markup),
    )
  }

  @action.bound
  public removeMany(ids: string[], callbackFn?: () => void) {
    if (!ids?.length) {
      return
    }

    ids.forEach(id => {
      const markup = this.byId.get(id)
      this.byId.delete(id)
      this.recentlyDeletedIds.add(id)
      this.eventsStore.dispatch(
        DELETE_MARKUP,
        id,
        () => this.recentlyDeletedIds.delete(id),
        () => this.handleDeleteFailure(markup),
      )
    })
    callbackFn?.()
  }
}
